import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Upload, FileSpreadsheet, Trash2, BarChart3, Table, RefreshCw, Database, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#a4de6c', '#d0ed57'];

const PowerBIReports = () => {
  const [activeTab, setActiveTab] = useState('system');
  const [systemData, setSystemData] = useState(null);
  const [selectedSystemReport, setSelectedSystemReport] = useState('documents_by_status');
  const [systemLoading, setSystemLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState('all');
  
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [viewMode, setViewMode] = useState('chart');
  const [chartType, setChartType] = useState('bar');
  const [chartXAxis, setChartXAxis] = useState('');
  const [chartYAxis, setChartYAxis] = useState('');
  const [reportName, setReportName] = useState('');

  const fetchSystemData = useCallback(async (packageFilter) => {
    try {
      setSystemLoading(true);
      const pkg = packageFilter || selectedPackage;
      const url = pkg && pkg !== 'all' 
        ? `${API}/reports/system/documents-summary?project_code=${encodeURIComponent(pkg)}`
        : `${API}/reports/system/documents-summary`;
      const response = await axios.get(url);
      setSystemData(response.data);
    } catch (error) {
      console.error('Failed to fetch system data:', error);
    } finally {
      setSystemLoading(false);
    }
  }, [selectedPackage]);

  const fetchReports = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports`);
      setReports(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSystemData();
    fetchReports();
  }, [fetchSystemData, fetchReports]);

  const handlePackageChange = (val) => {
    setSelectedPackage(val);
    fetchSystemData(val);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    if (reportName) formData.append('report_name', reportName);
    setUploading(true);
    try {
      await axios.post(`${API}/reports/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Report uploaded successfully!');
      setUploadDialogOpen(false);
      setReportName('');
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload report');
    } finally {
      setUploading(false);
    }
  };

  const handleSelectReport = async (report) => {
    setSelectedReport(report);
    try {
      const response = await axios.get(`${API}/reports/${report.id}`);
      setReportData(response.data);
      const firstSheet = response.data.sheet_names[0];
      setSelectedSheet(firstSheet);
      const sheetData = response.data.sheets[firstSheet];
      if (sheetData.columns.length > 0) {
        setChartXAxis(sheetData.columns[0]);
        if (sheetData.numeric_columns.length > 0) setChartYAxis(sheetData.numeric_columns[0]);
      }
    } catch (error) {
      toast.error('Failed to load report data');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    try {
      await axios.delete(`${API}/reports/${reportId}`);
      toast.success('Report deleted');
      fetchReports();
      if (selectedReport?.id === reportId) { setSelectedReport(null); setReportData(null); }
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const renderChartForData = (data, xKey, yKey, type = 'bar') => {
    if (!data || data.length === 0) return <div className="text-center text-gray-500 py-8">No data available</div>;
    const chartData = data.slice(0, 50);
    if (type === 'pie') {
      const pieData = chartData.map(row => ({ name: String(row[xKey] || 'N/A'), value: Number(row[yKey]) || 0 })).filter(d => d.value > 0);
      return (
        <ResponsiveContainer width="100%" height={350}>
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={120} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
              {pieData.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    if (type === 'line') {
      return (
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey={yKey} stroke="#8884d8" strokeWidth={2} dot={{ fill: '#8884d8' }} />
          </LineChart>
        </ResponsiveContainer>
      );
    }
    return (
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} interval={0} angle={-45} textAnchor="end" height={80} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          <Bar dataKey={yKey} fill="#8884d8" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderTableForData = (data, columns) => {
    if (!data || data.length === 0) return <div className="text-center text-gray-500 py-8">No data available</div>;
    return (
      <div className="overflow-auto max-h-[400px] border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0">
            <tr>{columns.map((col, idx) => (<th key={idx} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{col}</th>))}</tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, rowIdx) => (
              <tr key={rowIdx} className="hover:bg-gray-50">{columns.map((col, colIdx) => (<td key={colIdx} className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">{row[col] !== null && row[col] !== undefined ? String(row[col]) : '-'}</td>))}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const currentSheetData = reportData?.sheets?.[selectedSheet];
  const currentSystemReport = systemData?.reports?.[selectedSystemReport];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics & Reports</h2>
          <p className="text-gray-500">View system statistics and upload custom reports</p>
        </div>
        <div className="flex items-center gap-3">
          {systemData?.unique_packages?.length > 0 && (
            <Select value={selectedPackage} onValueChange={handlePackageChange}>
              <SelectTrigger className="w-52 h-9 text-sm" data-testid="package-filter-select">
                <SelectValue placeholder="All Packages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Packages</SelectItem>
                {systemData.unique_packages.map(pkg => (
                  <SelectItem key={pkg} value={pkg}>{pkg}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={() => { fetchSystemData(); fetchReports(); }}>
            <RefreshCw size={16} className="mr-2" />Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Database size={16} />System Reports
          </TabsTrigger>
          <TabsTrigger value="uploaded" className="flex items-center gap-2">
            <FileSpreadsheet size={16} />Uploaded Reports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="system" className="mt-6">
          {systemLoading ? (
            <div className="text-center py-16 text-gray-500">Loading system data...</div>
          ) : !systemData ? (
            <div className="text-center py-16 text-gray-500">Failed to load system data</div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-3xl font-bold text-blue-600">{systemData.total_documents}</div>
                    <div className="text-sm text-gray-500">Total Documents</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-3xl font-bold text-green-600">{systemData.total_cycles}</div>
                    <div className="text-sm text-gray-500">Review Cycles</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-3xl font-bold text-purple-600">{Object.keys(systemData.reports).length}</div>
                    <div className="text-sm text-gray-500">Available Reports</div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <Card className="lg:col-span-1">
                  <CardHeader><CardTitle className="text-lg">Available Reports</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(systemData.reports).map(([key, report]) => (
                        <div
                          key={key}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedSystemReport === key ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
                          onClick={() => setSelectedSystemReport(key)}
                        >
                          <div className="flex items-center gap-2">
                            <TrendingUp size={16} className="text-blue-600" />
                            <span className="font-medium text-sm">{report.name}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{report.data.length} items</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="lg:col-span-3">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-lg">{currentSystemReport?.name || 'Select a Report'}</CardTitle>
                      {currentSystemReport && (
                        <div className="flex border rounded-lg overflow-hidden">
                          <Button variant={viewMode === 'chart' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('chart')} className="rounded-none">
                            <BarChart3 size={16} className="mr-1" />Chart
                          </Button>
                          <Button variant={viewMode === 'table' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('table')} className="rounded-none">
                            <Table size={16} className="mr-1" />Table
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {currentSystemReport && (
                      viewMode === 'chart' 
                        ? renderChartForData(currentSystemReport.data, currentSystemReport.columns[0], currentSystemReport.columns[1], currentSystemReport.chart_type)
                        : renderTableForData(currentSystemReport.data, currentSystemReport.columns)
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="uploaded" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setUploadDialogOpen(true)}>
              <Upload size={16} className="mr-2" />Upload Report
            </Button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <Card className="lg:col-span-1">
              <CardHeader><CardTitle className="text-lg">Uploaded Reports</CardTitle></CardHeader>
              <CardContent>
                {loading ? <div className="text-center py-4 text-gray-500">Loading...</div> : reports.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileSpreadsheet size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No reports uploaded yet</p>
                    <Button variant="link" onClick={() => setUploadDialogOpen(true)}>Upload your first report</Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {reports.map((report) => (
                      <div key={report.id} className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedReport?.id === report.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} onClick={() => handleSelectReport(report)}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2"><FileSpreadsheet size={18} className="text-green-600" /><span className="font-medium text-sm truncate max-w-[150px]">{report.name}</span></div>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={(e) => { e.stopPropagation(); handleDeleteReport(report.id); }}><Trash2 size={14} /></Button>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{report.sheet_names?.length || 0} sheet(s) - {new Date(report.created_at).toLocaleDateString()}</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-3">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">{selectedReport ? selectedReport.name : 'Select a Report'}</CardTitle>
                  {selectedReport && (
                    <div className="flex items-center gap-2">
                      {reportData?.sheet_names?.length > 1 && (
                        <Select value={selectedSheet} onValueChange={setSelectedSheet}>
                          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Select sheet" /></SelectTrigger>
                          <SelectContent>{reportData.sheet_names.map((sheet) => (<SelectItem key={sheet} value={sheet}>{sheet}</SelectItem>))}</SelectContent>
                        </Select>
                      )}
                      <div className="flex border rounded-lg overflow-hidden">
                        <Button variant={viewMode === 'table' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('table')} className="rounded-none"><Table size={16} className="mr-1" />Table</Button>
                        <Button variant={viewMode === 'chart' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('chart')} className="rounded-none"><BarChart3 size={16} className="mr-1" />Chart</Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!selectedReport ? (
                  <div className="text-center py-16 text-gray-500"><BarChart3 size={64} className="mx-auto mb-4 opacity-30" /><p>Select a report from the list to view its data</p></div>
                ) : !reportData ? (
                  <div className="text-center py-16 text-gray-500">Loading report data...</div>
                ) : viewMode === 'table' ? (
                  renderTableForData(currentSheetData?.data, currentSheetData?.columns)
                ) : (
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">Chart Type:</Label>
                        <Select value={chartType} onValueChange={setChartType}>
                          <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="bar">Bar</SelectItem><SelectItem value="line">Line</SelectItem><SelectItem value="pie">Pie</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">X-Axis:</Label>
                        <Select value={chartXAxis} onValueChange={setChartXAxis}>
                          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                          <SelectContent>{currentSheetData?.columns.map((col) => (<SelectItem key={col} value={col}>{col}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">Y-Axis:</Label>
                        <Select value={chartYAxis} onValueChange={setChartYAxis}>
                          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                          <SelectContent>{currentSheetData?.numeric_columns.map((col) => (<SelectItem key={col} value={col}>{col}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    {renderChartForData(currentSheetData?.data, chartXAxis, chartYAxis, chartType)}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Report</DialogTitle><DialogDescription>Upload an Excel (.xlsx) or CSV file to display as charts and tables.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Report Name (optional)</Label><Input placeholder="My Report" value={reportName} onChange={(e) => setReportName(e.target.value)} /></div>
            <div className="space-y-2"><Label>Select File</Label><Input type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} disabled={uploading} /></div>
            {uploading && <div className="text-center text-sm text-gray-500">Uploading and processing...</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PowerBIReports;
